'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useForm } from 'react-hook-form';

import { ROUTES } from '@/constants/routes';

import { useRegisterMutation } from '@/modules/auth/hooks/use-register-mutation';
import {
  registerSchema,
  type RegisterFormValues,
} from '@/modules/auth/schemas/auth-forms.schema';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export default function RegisterPage() {
  const registerMutation = useRegisterMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      mobileNumber: '',
      password: '',
    },
  });

  const onSubmit = handleSubmit((values) => {
    registerMutation.mutate(values);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create your profile</CardTitle>
        <CardDescription>
          You will receive the standard CUSTOMER role until operational teams elevate access.
        </CardDescription>
      </CardHeader>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="First name" autoComplete="given-name" error={errors.firstName?.message} {...register('firstName')} />
          <Input label="Last name" autoComplete="family-name" error={errors.lastName?.message} {...register('lastName')} />
        </div>

        <Input label="Email" type="email" autoComplete="email" error={errors.email?.message} {...register('email')} />

        <Input
          label="Mobile number"
          hint="South African mobile numbers only — leading 0 or +27 accepted."
          autoComplete="tel"
          error={errors.mobileNumber?.message}
          {...register('mobileNumber')}
        />

        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <Button type="submit" className="w-full" loading={registerMutation.isPending}>
          Submit registration
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Already registered?{' '}
          <Link className="font-semibold text-primary underline-offset-4 hover:underline" href={ROUTES.login}>
            Sign in
          </Link>
        </p>

        <Alert variant="warning" title="Compliance reminder">
          <p>
            Provide accurate customer information — downstream financial onboarding relies on verified
            identities.
          </p>
        </Alert>
      </form>
    </Card>
  );
}
